import { inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { OperatorService } from '../services/operator/operator.service';
import { Observable, from } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  LeaderboardPeriodType,
  LeaderboardType,
} from '../services/leaderboards/models/leaderboard-metadata';
import { WAGER_ENDPOINTS } from '../api/player/player-api';
import { GameId } from 'src/app/shared/models/game/game-id';

// Type-only import for ActivityFeedMock
import type { ActivityFeedMock } from './mock-responses/activiy-feed.mock';

@Injectable()
export class DemoInterceptor implements HttpInterceptor {
  private operatorService = inject(OperatorService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (OperatorService.IsDemo) {
      return from(this.demoResponseForUrl(request.url)).pipe(
        catchError((error) => {
          if (this.isUnmockedApiRequest(request.url)) {
            console.warn(
              `DemoInterceptor: no mock for ${request.url} — falling through to the network`,
              error,
            );
          }
          return next.handle(request);
        }),
      );
    } else if (this.isSandboxRequest(request.url)) {
      return from(this.sandboxResponseForUrl(request.url)).pipe(
        catchError(() => next.handle(request)),
      );
    }

    return next.handle(request);
  }

  private isSandboxRequest(url: string): boolean {
    return url.toLowerCase().includes('sandbox');
  }

  /**
   * Anything that escapes the mock table is sent for real, and demo mode attaches no auth token
   * (AuthInterceptor skips it), so an unmocked API endpoint silently 401s — which is how the
   * rooms endpoints went unnoticed. Two kinds of request legitimately pass through and must stay
   * quiet: bundled assets (relative URLs — i18n, themes), and bootup, which is unauthenticated
   * and must stay live because it carries the feature flags demo mode reads.
   */
  private isUnmockedApiRequest(url: string): boolean {
    return /^https?:\/\//.test(url) && !url.includes(WAGER_ENDPOINTS.BOOTUP);
  }

  private async demoResponseForUrl(url: string): Promise<HttpResponse<unknown>> {
    if (url.includes(WAGER_ENDPOINTS.GAME_STATS)) {
      const { GAME_STATS_MOCK } = await import('./mock-responses/game-stats.mock');
      return new HttpResponse(GAME_STATS_MOCK);
    }
    if (url.includes(WAGER_ENDPOINTS.TIERS)) {
      const { TIERS_MOCK } = await import('./mock-responses/tiers.mock');
      return new HttpResponse(TIERS_MOCK);
    }
    if (url.includes(WAGER_ENDPOINTS.WAITING_OPPONENTS)) {
      const { WAITING_OPPONENTS_MOCK } = await import('./mock-responses/waiting-opponents.mock');
      return new HttpResponse(WAITING_OPPONENTS_MOCK);
    }
    if (url.includes(WAGER_ENDPOINTS.GAME_LEADERBOARDS)) {
      return this.getLeaderboardResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.ACTIVITY_FEED)) {
      return this.getActivityFeedResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.PRACTICE_ROUNDS)) {
      return this.getPracticeRoundsResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.GENERATE_SESSION_TOKEN)) {
      return new HttpResponse({
        status: 200,
        body: {
          status: 200,
          msg: 'OK',
          data: {
            token: 'demo',
            userId: 'demo',
          },
        },
      });
    }
    if (url.includes(WAGER_ENDPOINTS.BALANCE)) {
      return new HttpResponse({
        status: 200,
        body: {
          status: 200,
          msg: 'OK',
          data: {
            balance: this.operatorService.getDemoBalance() ?? 0,
            currency: 'USD',
          },
        },
      });
    }

    throw new Error(`No mock response for url: ${url}`);
  }

  private async sandboxResponseForUrl(url: string): Promise<HttpResponse<unknown>> {
    if (url.includes(WAGER_ENDPOINTS.GAME_LEADERBOARDS)) {
      return this.getLeaderboardResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.ACTIVITY_FEED)) {
      return this.getActivityFeedResponse(url);
    }
    if (url.includes(WAGER_ENDPOINTS.GAME_STATS)) {
      const { GAME_STATS_MOCK } = await import('./mock-responses/game-stats.mock');
      return new HttpResponse(GAME_STATS_MOCK);
    }

    throw new Error(`No sandbox response for url: ${url}`);
  }

  private async getLeaderboardResponse(url: string): Promise<HttpResponse<unknown>> {
    const leaderboards = await import('./mock-responses/leaderboards.mock');

    if (url.includes(LeaderboardType.MostWins)) {
      if (url.includes(LeaderboardPeriodType.Daily)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTWINS_1DAY);
      }
      if (url.includes(LeaderboardPeriodType.Weekly)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTWINS_7DAY);
      }
      if (url.includes(LeaderboardPeriodType.Monthly)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTWINS_30DAY);
      }
      if (url.includes(LeaderboardPeriodType.AllTime)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTWINS_ALLTIME);
      }
    } else if (url.includes(LeaderboardType.MostMoneyWon)) {
      if (url.includes(LeaderboardPeriodType.Daily)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTMONEY_1DAY);
      }
      if (url.includes(LeaderboardPeriodType.Weekly)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTMONEY_7DAY);
      }
      if (url.includes(LeaderboardPeriodType.Monthly)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTMONEY_30DAY);
      }
      if (url.includes(LeaderboardPeriodType.AllTime)) {
        return new HttpResponse(leaderboards.LEADERBOARD_MOSTMONEY_ALLTIME);
      }
    }
    throw new Error(`No leaderboard response for url: ${url}`);
  }

  private async getActivityFeedResponse(url: string): Promise<HttpResponse<unknown>> {
    const activityFeeds = await import('./mock-responses/activiy-feed.mock');

    let activityMock: ActivityFeedMock | undefined = undefined;
    if (url.includes(GameId.Solitaire)) {
      activityMock = activityFeeds.ACTIVITY_FEED_SOLITAIRE_MOCK;
    } else if (url.includes(GameId.Blocks)) {
      activityMock = activityFeeds.ACTIVITY_FEED_BLOCKS_MOCK;
    } else if (url.includes(GameId.Blackjack)) {
      activityMock = activityFeeds.ACTIVITY_FEED_BLACKJACK_MOCK;
    } else if (url.includes(GameId.TwentyOne)) {
      activityMock = activityFeeds.ACTIVITY_FEED_TWENTYONE_MOCK;
    } else if (url.includes(GameId.Slots)) {
      activityMock = activityFeeds.ACTIVITY_FEED_SLOTS_MOCK;
    } else if (url.includes(GameId.VideoPoker)) {
      activityMock = activityFeeds.ACTIVITY_FEED_VIDEOPOKER_MOCK;
    } else if (url.includes(GameId.HiLo)) {
      activityMock = activityFeeds.ACTIVITY_FEED_HILO_MOCK;
    } else if (url.includes(GameId.Bingo)) {
      activityMock = activityFeeds.ACTIVITY_FEED_BINGO_MOCK;
    }
    if (activityMock) {
      this.updateActivityFeedDates(activityMock);
      return new HttpResponse(activityMock);
    }
    throw new Error(`No activity feed response for url: ${url}`);
  }

  private async getPracticeRoundsResponse(url: string): Promise<HttpResponse<unknown>> {
    if (url.includes(GameId.Solitaire)) {
      const { PRACTICE_ROUNDS_SOLITAIRE_MOCK } =
        await import('./mock-responses/practice-rounds-solitaire.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_SOLITAIRE_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_SOLITAIRE_MOCK[randomIndex]);
    } else if (url.includes(GameId.Blocks)) {
      const { PRACTICE_ROUNDS_BLOCKS_MOCK } =
        await import('./mock-responses/practice-rounds-blocks.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_BLOCKS_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_BLOCKS_MOCK[randomIndex]);
    } else if (url.includes(GameId.Blackjack)) {
      const { PRACTICE_ROUNDS_BLACKJACK_MOCK } =
        await import('./mock-responses/practice-rounds-blackjack.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_BLACKJACK_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_BLACKJACK_MOCK[randomIndex]);
    } else if (url.includes(GameId.TwentyOne)) {
      const { PRACTICE_ROUNDS_TWENTYONE_MOCK } =
        await import('./mock-responses/practice-rounds-twentyone.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_TWENTYONE_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_TWENTYONE_MOCK[randomIndex]);
    } else if (url.includes(GameId.Slots)) {
      const { PRACTICE_ROUNDS_SLOTS_MOCK } =
        await import('./mock-responses/practice-rounds-slots.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_SLOTS_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_SLOTS_MOCK[randomIndex]);
    } else if (url.includes(GameId.VideoPoker)) {
      const { PRACTICE_ROUNDS_VIDEOPOKER_MOCK } =
        await import('./mock-responses/practice-rounds-videopoker.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_VIDEOPOKER_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_VIDEOPOKER_MOCK[randomIndex]);
    } else if (url.includes(GameId.Plinko)) {
      const { PRACTICE_ROUNDS_PLINKO_MOCK } =
        await import('./mock-responses/practice-rounds-plinko.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_PLINKO_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_PLINKO_MOCK[randomIndex]);
    } else if (url.includes(GameId.HiLo)) {
      const { PRACTICE_ROUNDS_HILO_MOCK } =
        await import('./mock-responses/practice-rounds-hilo.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_HILO_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_HILO_MOCK[randomIndex]);
    } else if (url.includes(GameId.Bingo)) {
      const { PRACTICE_ROUNDS_BINGO_MOCK } =
        await import('./mock-responses/practice-rounds-bingo.mock');
      const randomIndex = this.getRandomIndex(PRACTICE_ROUNDS_BINGO_MOCK);
      return new HttpResponse(PRACTICE_ROUNDS_BINGO_MOCK[randomIndex]);
    }
    throw new Error(`No practice rounds response for url: ${url}`);
  }

  private getRandomIndex<T>(array: T[]): number {
    return Math.floor(Math.random() * array.length);
  }

  /**
   * Iterates over each activity record and updates its createdAt timestamp
   * to have today's date while keeping the original time.
   */
  private updateActivityFeedDates(activityFeed: ActivityFeedMock): void {
    if (activityFeed && activityFeed.body.data && activityFeed.body.data.activities) {
      let minutesAgo = 1;
      Object.keys(activityFeed.body.data.activities).forEach((timeKey) => {
        const activityGroup = activityFeed.body.data.activities[timeKey];
        Object.keys(activityGroup).forEach((activityKey) => {
          const activity = activityGroup[activityKey];
          if (activity.createdAt) {
            const now = new Date();
            const updatedDate = new Date(now.getTime() - minutesAgo * 60 * 1000);
            activity.createdAt = updatedDate.getTime();
            minutesAgo++;
          }
        });
      });
    }
  }
}
